import { useTranslation } from 'react-i18next';
import { useState, useCallback } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import ScrollToTop from '@/components/base/ScrollToTop';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9otihiftmtnfcfh0du0';

export default function About() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const breadcrumbItems = [{ label: t('about.title') }];

  const faqData = [
    {
      q: '韩漫漫画网站上的漫画都是免费的吗？',
      a: '是的，韩漫漫画网站上所有的韩国漫画都是完全免费的。我们致力于为广大漫画爱好者提供零门槛的在线阅读体验。无论是最新连载的热门韩漫还是经典的完结作品，您都可以免费畅享，无需任何付费订阅。',
    },
    {
      q: '网站上的漫画多久更新一次？',
      a: '我们与多家韩国漫画平台保持紧密合作，漫画更新频率与其源头保持一致。热门连载作品通常会每日或每周更新，确保读者能够第一时间阅读到最新的章节内容。您可以在每个漫画的详情页查看具体的更新计划。',
    },
    {
      q: '如何在手机上阅读漫画？',
      a: '韩漫漫画网站采用了响应式设计，完美适配手机、平板和电脑等多种设备。您只需要使用手机浏览器访问我们的网站，即可享受优化的移动端阅读体验。网页会自动适配您的屏幕尺寸，提供流畅的滚动阅读模式。',
    },
    {
      q: '韩漫漫画网站支持哪些漫画类型？',
      a: '我们涵盖了丰富的韩国漫画类型，包括但不限于恋爱、奇幻、动作、悬疑、搞笑、剧情、耽美等多个分类。无论您喜欢浪漫甜蜜的恋爱故事、惊心动魄的动作冒险，还是脑洞大开的奇幻世界，都能在这里找到心仪的作品。',
    },
  ];

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormMessage('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && typeof honeypot === 'string' && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormMessage('留言已发送，感谢您的反馈！');
      form.reset();
      return;
    }
    formData.delete('website_alt');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });

      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not JSON
      }

      if (response.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setFormStatus('success');
        setFormMessage('留言已发送，感谢您的反馈！');
        form.reset();
      } else {
        const serverMsg =
          (parsed && (parsed as Record<string, unknown>).meta && ((parsed as Record<string, unknown>).meta as Record<string, unknown>).message) ||
          (parsed && (parsed as Record<string, unknown>).message) ||
          responseText ||
          '提交失败，请稍后重试';
        const msgStr = typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试';
        if (msgStr.includes('spam') || msgStr.includes('form data is spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后重试');
        } else {
          setFormStatus('error');
          setFormMessage(msgStr);
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络后重试');
    }
  }, []);

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于我们 - 韩漫漫画网站',
          description: '了解韩漫漫画网站的品牌故事、用户口碑和联系方式。韩漫漫画网站是免费韩漫与漫画大全的宝库。',
          url: window.location.origin + '/about',
          isPartOf: { '@type': 'WebSite', name: '韩漫漫画网站', url: window.location.origin },
        }}
      />
      <SchemaOrg
        type="FAQPage"
        data={{
          mainEntity: faqData.map((item) => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.a,
            },
          })),
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb items={breadcrumbItems} />

          <article className="max-w-[1400px] mx-auto px-4 md:px-6 py-10">
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">{t('about.title')}</h1>
            <p className="text-sm text-foreground-500 mb-12 max-w-3xl">
              了解一下韩漫漫画网站的故事，看看我们如何成为无数韩漫爱好者最信赖的在线漫画平台。
            </p>

            {/* Brand Introduction */}
            <section className="mb-16">
              <h2 className="font-heading text-xl text-foreground-950 mb-6 flex items-center gap-2">
                <i className="ri-building-line text-primary-500"></i>
                {t('about.brand')}
              </h2>
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8">
                <div className="flex flex-col md:flex-row gap-8">
                  <div className="flex-1">
                    <h3 className="font-heading text-lg text-foreground-950 mb-4">韩漫漫画网站的故事</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      韩漫漫画网站诞生于一群韩国漫画深度爱好者的共同梦想。我们发现，虽然韩国漫画在全球范围内越来越受欢迎，但高质量的中文韩漫阅读平台却十分匮乏。于是，我们决心创建一个真正为中文读者服务的韩漫平台。
                    </p>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      自成立以来，韩漫漫画网站始终秉持"免费、全面、优质"的核心理念。我们不断拓展漫画资源库，从最初几十部经典韩漫，发展到如今涵盖恋爱、奇幻、动作、悬疑、搞笑等七大分类、数百部作品的规模。
                    </p>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      我们深知，每一位读者都希望找到好看的韩国漫画。因此，我们的编辑团队每天都会精心筛选和推荐优质作品，同时保持与韩国源头同步的更新速度，让你不错过任何精彩章节。
                    </p>
                  </div>
                  <div className="md:w-80 shrink-0">
                    <div className="rounded-xl overflow-hidden h-48 md:h-full min-h-[200px]">
                      <img
                        src="https://readdy.ai/api/search-image?query=A%20modern%20cozy%20reading%20space%20with%20a%20tablet%20displaying%20colorful%20webtoon%20comic%20art%2C%20warm%20ambient%20lighting%20from%20a%20desk%20lamp%2C%20bookshelves%20in%20background%2C%20hot%20coffee%20cup%2C%20plant%20decoration%2C%20soft%20warm%20tones%2C%20artistic%20lifestyle%20photography%20style%2C%20clean%20and%20inviting%20aesthetic&width=600&height=500&seq=about-brand-manhwa&orientation=landscape"
                        alt="韩漫漫画网站 - 在线漫画阅读体验"
                        title="韩漫漫画网站 - 免费韩漫阅读体验"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Reputation Section */}
            <section className="mb-16">
              <h2 className="font-heading text-xl text-foreground-950 mb-6 flex items-center gap-2">
                <i className="ri-medal-line text-accent-500"></i>
                {t('about.reputation')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                    <i className="ri-user-heart-line text-2xl text-accent-600"></i>
                  </div>
                  <h3 className="font-heading text-lg text-foreground-950 mb-2">海量用户信赖</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    韩漫漫画网站已累计服务超过数百万韩漫爱好者，日活跃用户持续增长。我们为读者提供了数百部优质韩国漫画的免费在线阅读服务，成为中文韩漫领域的标杆平台。
                  </p>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                    <i className="ri-speed-line text-2xl text-accent-600"></i>
                  </div>
                  <h3 className="font-heading text-lg text-foreground-950 mb-2">更新快速及时</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    我们与韩国漫画源头保持紧密同步，热门连载作品的更新速度在行业内处于领先地位。读者反馈显示，超过95%的用户对更新频率感到满意。
                  </p>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                    <i className="ri-shield-check-line text-2xl text-accent-600"></i>
                  </div>
                  <h3 className="font-heading text-lg text-foreground-950 mb-2">优质阅读体验</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    韩漫漫画网站采用先进的前端技术，实现了流畅的滚动阅读和智能的页面适配。无论是手机、平板还是电脑，都能享受优质的在线漫画阅读体验。
                  </p>
                </div>
              </div>
            </section>

            {/* Contact Section */}
            <section className="mb-10">
              <h2 className="font-heading text-xl text-foreground-950 mb-6 flex items-center gap-2">
                <i className="ri-mail-line text-secondary-500"></i>
                {t('about.contact')}
              </h2>
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-5">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                        <i className="ri-mail-line text-primary-600"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-foreground-950">电子邮箱</h4>
                        <p className="text-sm text-foreground-500">contact@manhuakr.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                        <i className="ri-phone-line text-primary-600"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-foreground-950">客服热线</h4>
                        <p className="text-sm text-foreground-500">400-123-4567</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                        <i className="ri-time-line text-primary-600"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-foreground-950">工作时间</h4>
                        <p className="text-sm text-foreground-500">周一至周五 9:00-18:00（法定节假日除外）</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                        <i className="ri-map-pin-line text-primary-600"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-foreground-950">公司地址</h4>
                        <p className="text-sm text-foreground-500">上海市浦东新区张江高科技园区碧波路888号</p>
                      </div>
                    </div>
                  </div>

                  {/* Contact Form */}
                  <div className="bg-background-100 rounded-xl p-6">
                    <h3 className="font-heading text-base text-foreground-950 mb-4">给我们留言</h3>
                    <form
                      id="contact-form"
                      data-readdy-form=""
                      className="space-y-4"
                      onSubmit={handleSubmit}
                    >
                      <div>
                        <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1">姓名</label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-300 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1">邮箱</label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-300 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-1">留言内容</label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-300 transition-colors resize-none"
                          placeholder="请输入您的留言内容（最多500字）"
                        ></textarea>
                      </div>
                      <input
                        type="text"
                        name="website_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                      {formMessage && (
                        <p className={`text-sm ${formStatus === 'success' ? 'text-primary-600' : 'text-red-500'}`}>
                          {formMessage}
                        </p>
                      )}
                      <button
                        type="submit"
                        disabled={formStatus === 'submitting'}
                        className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
                      >
                        {formStatus === 'submitting' ? '发送中...' : '发送留言'}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </section>

            {/* FAQ Section */}
            <section>
              <h2 className="font-heading text-xl text-foreground-950 mb-6 flex items-center gap-2">
                <i className="ri-question-answer-line text-primary-500"></i>
                常见问题
              </h2>
              <div className="space-y-3">
                {faqData.map((item, index) => (
                  <details key={index} className="bg-background-50 border border-background-200/70 rounded-xl group">
                    <summary className="px-6 py-4 cursor-pointer flex items-center justify-between font-medium text-sm text-foreground-800 hover:text-primary-600 transition-colors">
                      {item.q}
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <div className="px-6 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </section>
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}